(() => {
  'use strict';
  const root = document.querySelector('[data-fp-assistant]');
  if (!root || root.dataset.initialized) return;
  root.dataset.initialized = 'true';
  document.body.append(root); // Keep the floating panel outside the sticky header's stacking context.
  const el = name => root.querySelector(`[data-assistant-${name}]`);
  const panel = el('dialog');
  const atelier = root.hasAttribute('data-atelier-a');
  const smallScreen = matchMedia(atelier ? '(max-width:760px)' : '(max-width:600px)');
  const emptyPlaceholder = atelier ? window.FivePointI18n.t("Type a reply…") : window.FivePointI18n.t("Describe a fabric…");
  const endpoint = root.dataset.endpoint;
  const storeKey = `fivepoint-assistant-v1:${root.dataset.country}:${root.dataset.currency}`;
  const viewKey = `${storeKey}:view`;
  const draftKey = `${storeKey}:draft`;
  const money = new Intl.NumberFormat(root.dataset.locale, { style: 'currency', currency: root.dataset.currency });
  const cards = new Map();
  let conversation = { token: null, revision: 0, messages: [], selected: [], expires: 0 };
  let file = null, previewURL = null, photoPreview = null, preparingPhoto = false, attachmentEpoch = 0;
  let busy = false, adding = false, uncertainCart = false;
  let resetting = false, cancelling = false, recoveryRetryAfter = 0, sessionCreation = null;
  let request = null, pending = null, previousFocus = null, restoring = false;
  let latestHandles = new Set();
  let previewEpoch = 0;
  let opened = false;
  let restoreProducts = async () => {};
  const photoURLs = new Set();
  const maxPreviewChars = 32768, maxPreviews = 24, maxStoredChars = 2097152;
  // Owned additions stay out of the theme's strict translation-key dictionary.
  const uxCopies = {
    en: { queued: 'Your message is saved and waiting for an available slot. You can check the reply later.', paused: 'Your message is saved. AI replies are temporarily paused; please check back later.', checkQueued: 'Your message is saved. Select Check reply later to see its progress; please do not submit it again.', processing: 'Processing your request…', ticket: 'Submitting your sourcing request and notifying our team…', preparing: 'Your photo is still being prepared. Please wait.', preview: 'Your photo can still be sent, but its preview could not be saved for refresh.', quota: 'This conversation is saved, but photo previews could not be saved for refresh.', unsaved: 'This conversation can continue here, but could not be saved. Keep this page open until the reply finishes.', limit: 'Some photo previews could not be restored. The conversation and uploaded references are unchanged.' },
    zh: { queued: '您的消息已保存，正在排队。您可以稍后查看回复。', paused: '您的消息已保存，AI 回复暂时暂停，请稍后查看。', checkQueued: '您的消息已保存，请稍后点击查看回复，不必重复提交。', processing: '正在处理您的请求…', ticket: '正在提交找版请求并通知团队…', preparing: '照片仍在处理中，请稍候。', preview: '照片仍可发送，但无法保存刷新后使用的预览。', quota: '对话已保存，但照片预览无法在刷新后保留。', unsaved: '您可以继续当前对话，但暂时无法保存。请保持页面打开，直到回复完成。', limit: '部分照片预览无法恢复。对话和已上传的参考照片保持不变。' },
    ms: { queued: 'Mesej anda telah disimpan dan sedang menunggu giliran. Anda boleh menyemak balasan kemudian.', paused: 'Mesej anda telah disimpan. Balasan AI dijeda sementara; sila semak semula kemudian.', checkQueued: 'Mesej anda telah disimpan. Semak balasan kemudian; jangan hantar mesej yang sama sekali lagi.', processing: 'Sedang memproses permintaan anda…', ticket: 'Sedang menghantar permintaan carian fabrik dan memaklumkan pasukan kami…', preparing: 'Foto anda masih sedang disediakan. Sila tunggu.', preview: 'Foto masih boleh dihantar, tetapi pratontonnya tidak dapat disimpan untuk muat semula.', quota: 'Perbualan disimpan, tetapi pratonton foto tidak dapat disimpan untuk muat semula.', unsaved: 'Anda boleh meneruskan perbualan ini, tetapi ia tidak dapat disimpan. Biarkan halaman ini terbuka sehingga balasan selesai.', limit: 'Sesetengah pratonton foto tidak dapat dipulihkan. Perbualan dan foto rujukan yang dimuat naik tidak berubah.' }
  };
  const ux = uxCopies[(root.dataset.locale || 'en').toLowerCase().split('-')[0]] || uxCopies.en;
  const recoveryCopies = {
    en: { waiting: 'Waiting for your previous reply. You can type your next message here.', checking: 'Checking your previous reply…', stopped: 'Waiting is paused. Select Check reply to continue. Your draft is kept.', offline: 'You are offline. Your draft is kept; we will check the previous reply when you reconnect.' },
    zh: { waiting: '正在等待上一条回复。您可以先输入下一条消息。', checking: '正在取回上一条回复…', stopped: '已暂停等待。点击“查看回复”即可继续，草稿已保留。', offline: '网络已断开。草稿已保留，重新联网后会自动检查上一条回复。' },
    ms: { waiting: 'Menunggu balasan sebelumnya. Anda boleh menaip mesej seterusnya di sini.', checking: 'Menyemak balasan sebelumnya…', stopped: 'Penungguan dijeda. Pilih Semak balasan untuk meneruskan. Draf anda disimpan.', offline: 'Anda di luar talian. Draf disimpan; kami akan menyemak balasan sebelumnya apabila sambungan kembali.' }
  };
  const recoveryCopy = recoveryCopies[(root.dataset.locale || 'en').toLowerCase().split('-')[0]] || recoveryCopies.en;
  const cancellationCopies = {
    en: { label: 'Cancel reply', stopping: 'Cancelling this reply… Your edited message is kept.', retry: 'Retry cancellation', failed: 'Cancellation could not be confirmed. Your draft is kept; retry cancellation to continue.', done: 'Reply cancelled. Edit your message and send it when ready.', stopped: 'Stopped', offline: 'You are offline. Your draft is kept; cancellation will retry when you reconnect.', photo: 'Please attach the photo again with your corrected message.' },
    zh: { label: '取消回复', stopping: '正在取消本轮回复，已保留您修改的文字…', retry: '重试取消', failed: '暂时无法确认取消结果。草稿已保留，请重试取消后继续。', done: '本轮回复已取消，您可以修改并重新发送。', stopped: '已停止', offline: '网络已断开，草稿已保留，联网后将重试取消。', photo: '请在发送更正消息时重新附上照片。' },
    ms: { label: 'Batalkan balasan', stopping: 'Membatalkan balasan… Mesej yang diedit disimpan.', retry: 'Cuba pembatalan semula', failed: 'Pembatalan belum dapat disahkan. Draf disimpan; cuba pembatalan semula.', done: 'Balasan dibatalkan. Edit mesej dan hantar apabila sedia.', stopped: 'Dihentikan', offline: 'Anda di luar talian. Draf disimpan; pembatalan akan dicuba apabila bersambung semula.', photo: 'Lampirkan foto semula dengan mesej yang dibetulkan.' }
  };
  const cancellationCopy = cancellationCopies[(root.dataset.locale || 'en').toLowerCase().split('-')[0]] || cancellationCopies.en;
  el('cancel').setAttribute('aria-label', cancellationCopy.label);
  const recoveryNotice = document.createElement('p');
  recoveryNotice.className = 'fp-assistant__upload-note'; recoveryNotice.dataset.assistantRecoveryNotice = ''; recoveryNotice.setAttribute('role', 'status'); recoveryNotice.hidden = true;
  el('error').after(recoveryNotice);
  const offline = () => typeof navigator !== 'undefined' && navigator.onLine === false;
  function saveDraft() {
    try {
      sessionStorage.removeItem(draftKey);
      const text = el('input').value;
      if (text && text.length <= 3000) sessionStorage.setItem(draftKey, JSON.stringify({ text, expires: Date.now() + 1800000 }));
    } catch { /* A storage restriction must not erase the in-memory draft. */ }
  }
  function restoreDraft() {
    try {
      const raw = sessionStorage.getItem(draftKey);
      const saved = raw && raw.length < 20000 ? JSON.parse(raw) : null;
      if (saved && typeof saved.text === 'string' && saved.text.length <= 3000 && Number.isFinite(saved.expires) && saved.expires > Date.now() && saved.expires <= Date.now() + 1800000) el('input').value = saved.text;
      else sessionStorage.removeItem(draftKey);
    } catch { /* Keep the composer usable when browser storage is unavailable. */ }
  }
  const previewNotice = document.createElement('p');
  previewNotice.className = 'fp-assistant__upload-note'; previewNotice.dataset.assistantPreviewNotice = ''; previewNotice.setAttribute('role', 'status'); previewNotice.hidden = true;
  el('attachment').after(previewNotice);
  function photoNotice(text) { previewNotice.textContent = text; previewNotice.hidden = !text; }
  function safePhotoPreview(value) {
    if (typeof value !== 'string' || value.length > maxPreviewChars || !/^data:image\/jpeg;base64,[A-Za-z0-9+/]+={0,2}$/.test(value)) return false;
    let bytes; try { bytes = atob(value.slice(23)); } catch { return false; }
    const at = i => bytes.charCodeAt(i);
    if (bytes.length < 16 || at(0) !== 255 || at(1) !== 216 || at(2) !== 255 || at(bytes.length - 2) !== 255 || at(bytes.length - 1) !== 217) return false;
    // Check bounded JPEG dimensions before assigning a restored image src.
    let pos = 2, dimensions = false;
    while (pos + 4 < bytes.length) {
      if (at(pos++) !== 255) return false;
      while (at(pos) === 255) pos++;
      const marker = at(pos++);
      if (marker === 218) return dimensions;
      if (marker === 217 || marker === 0 || marker === 216) return false;
      const size = (at(pos) << 8) | at(pos + 1);
      if (size < 2 || pos + size > bytes.length) return false;
      if ([192, 193, 194].includes(marker)) {
        if (size < 8 || dimensions) return false;
        const height = (at(pos + 3) << 8) | at(pos + 4), width = (at(pos + 5) << 8) | at(pos + 6);
        if (!width || !height || width > 384 || height > 384) return false;
        dimensions = true;
      }
      pos += size;
    }
    return false;
  }
  function boundedPhotoMessages(messages) {
    let count = 0, reduced = false;
    const out = messages.map(item => ({ ...item }));
    for (let i = out.length - 1; i >= 0; i--) {
      if (!out[i].photo) continue;
      if (out[i].role === 'user' && safePhotoPreview(out[i].photo) && count++ < maxPreviews) continue;
      if (out[i].photo !== 'attached') reduced = true;
      out[i].photo = 'attached';
    }
    if (reduced) photoNotice(ux.limit);
    return out;
  }
  const handleOK = h => typeof h === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/.test(h);
  const button = (text, action) => {
    const node = document.createElement('button'); node.type = 'button'; node.textContent = text;
    node.dataset.assistantAction = action; return node;
  };
  function announce(text) { el('status').textContent = text; }
  function error(text) { el('error').textContent = text; el('error').hidden = !text; }
  function rememberView(isOpen) {
    try { sessionStorage.setItem(viewKey, JSON.stringify({ open: isOpen, path: location.pathname, savedAt: Date.now() })); }
    catch { announce(window.FivePointI18n.t("The assistant window position could not be saved for page navigation.")); }
  }
  function shouldResumeView() {
    try {
      const view = JSON.parse(sessionStorage.getItem(viewKey) || 'null');
      if (!view || view.open !== true || typeof view.path !== 'string' || !Number.isFinite(view.savedAt) || Date.now() - view.savedAt > 1800000) return false;
      // Let the destination page remain visible on phones; the same conversation
      // stays available in the launcher. A refresh of this page keeps it open.
      if (smallScreen.matches && view.path !== location.pathname) { rememberView(false); return false; }
      return true;
    } catch { announce(window.FivePointI18n.t("The assistant window state could not be restored. You can reopen it here.")); return false; }
  }
  function persist() {
    conversation.pending = pending ? { id: pending.id, text: pending.text, action: pending.action, page: pending.page, hasPhoto: Boolean(pending.file || pending.hasPhoto), submitted: pending.submitted === true, cancelRequested: pending.cancelRequested === true } : null;
    conversation.uncertainCart = uncertainCart;
    const stored = { ...conversation, messages: boundedPhotoMessages(conversation.messages) };
    try {
      // Remove old state first: a failed write must not revive stale pending turns.
      sessionStorage.removeItem(storeKey);
      if (conversation.token) {
        const serialized = JSON.stringify(stored);
        if (serialized.length > maxStoredChars) throw new Error('state exceeds storage budget');
        sessionStorage.setItem(storeKey, serialized);
      }
    } catch {
      try {
        sessionStorage.removeItem(storeKey);
        if (conversation.token) {
          stored.messages = stored.messages.map(item => ({ ...item, photo: item.photo ? 'attached' : null }));
          const serialized = JSON.stringify(stored);
          if (serialized.length > maxStoredChars) throw new Error('state exceeds storage budget');
          sessionStorage.setItem(storeKey, serialized);
          photoNotice(ux.quota);
        }
      } catch {
        try { sessionStorage.removeItem(storeKey); } catch { /* Browser storage is inaccessible. */ }
        photoNotice(ux.unsaved);
      }
    }
    rememberView(panel.open);
  }
  function restoreState() {
    try {
      const raw = sessionStorage.getItem(storeKey);
      if (raw && raw.length > maxStoredChars) throw new Error('oversized state');
      const stored = JSON.parse(raw || 'null');
      if (!stored) return;
      if (!Number.isFinite(stored.expires) || stored.expires <= Date.now()) { sessionStorage.removeItem(storeKey); return; }
      if (typeof stored.token !== 'string' || !/^[a-f0-9]{64}$/.test(stored.token) || !Array.isArray(stored.messages) || stored.messages.length > 50 || !Array.isArray(stored.selected) || stored.selected.length > 12 || !stored.selected.every(handleOK) || !Number.isInteger(stored.revision)) throw new Error('invalid state');
      if (stored.messages.some(m => !m || !['user','assistant'].includes(m.role) || typeof m.text !== 'string' || (m.handles && (!Array.isArray(m.handles) || m.handles.length > 4 || !m.handles.every(handleOK))))) throw new Error('invalid messages');
      if (stored.pending && (!/^[a-f0-9-]{36}$/.test(stored.pending.id) || typeof stored.pending.text !== 'string')) throw new Error('invalid pending reply');
      stored.messages = boundedPhotoMessages(stored.messages);
      conversation = stored;
      if (stored.pending) { pending = { ...stored.pending, recover: true, cancelRequested: stored.pending.cancelRequested === true || stored.pending.paused === true, paused: false }; el('retry').hidden = false; }
      uncertainCart = Boolean(stored.uncertainCart); el('cart-review').hidden = !uncertainCart;
    } catch {
      try { sessionStorage.removeItem(storeKey); } catch { /* Browser storage is inaccessible. */ }
      error(window.FivePointI18n.t("The previous conversation could not be restored. Please start a new one."));
    }
  }
  function open(trigger) {
    if (panel.open) return;
    previousFocus = trigger || document.activeElement;
    el('entry').hidden = true;
    smallScreen.matches ? panel.showModal() : panel.show();
    document.body.classList.toggle('fp-assistant-mobile-open', smallScreen.matches);
    document.querySelectorAll('[data-assistant-open],[data-fp-assistant-open]').forEach(n => n.setAttribute('aria-expanded', 'true'));
    el('hint').hidden = true;
    try { sessionStorage.setItem('fivepoint-assistant-hint', 'dismissed'); } catch { /* Optional hint only. */ }
    if (!restoring && !opened) el('history').scrollTop = el('history').scrollHeight;
    opened = true;
    rememberView(true);
    restoreProducts();
    resumePending();
  }
  function close() { rememberView(false); if (panel.open) panel.close(); }
  panel.addEventListener('keydown', event => { if (event.key === 'Escape') { event.preventDefault(); close(); } });
  panel.addEventListener('close', () => {
    if (panel.open) return; // A responsive switch may have already reopened the same dialog.
    rememberView(false);
    document.body.classList.remove('fp-assistant-mobile-open');
    el('entry').hidden = Boolean(document.querySelector('#cart-drawer dialog[open]'));
    document.querySelectorAll('[data-assistant-open],[data-fp-assistant-open]').forEach(n => n.setAttribute('aria-expanded', 'false'));
    if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
  });
  smallScreen.addEventListener('change', () => { if (panel.open) { close(); open(previousFocus); } });
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-fp-assistant-open]');
    if (trigger) { event.preventDefault(); open(trigger); }
  });
  document.addEventListener('fivepoint:assistant-compose', event => {
    const text = event.detail?.text;
    if (typeof text !== 'string' || !text.trim() || text.length > 3000) return;
    open(event.detail.trigger);
    if (busy || adding || pending || el('input').value.trim()) {
      error(window.FivePointI18n.t("Finish your current message first, then choose Refine with Ask FivePoint again."));
      return;
    }
    el('input').value = text;
    el('input').dispatchEvent(new Event('input'));
    el('input').focus();
  });
  const bagDialog = document.querySelector('#cart-drawer dialog');
  if (bagDialog) new MutationObserver(() => {
    if (bagDialog.open) { close(); el('entry').hidden = true; }
    else el('entry').hidden = panel.open;
  }).observe(bagDialog, { attributes: true, attributeFilter: ['open'] });

  function setBusy(value) {
    busy = value;
    const blocked = value || Boolean(pending) || cancelling || adding || resetting;
    el('send').hidden = value; el('send').disabled = blocked || preparingPhoto;
    el('cancel').hidden = !pending || pending.cancelBlocked === true; el('cancel').disabled = cancelling;
    el('input').disabled = adding || resetting;
    el('photo').disabled = blocked;
    root.querySelectorAll('[data-assistant-action],[data-assistant-starter]').forEach(n => { n.disabled = blocked; });
    el('new').disabled = value || cancelling || adding || resetting;
    el('retry').hidden = !pending || value || cancelling || resetting;
    el('retry').textContent = pending?.cancelRequested ? cancellationCopy.retry : window.FivePointI18n.t("Check reply");
    recoveryNotice.hidden = !pending;
    recoveryNotice.textContent = !pending ? '' : pending.cancelRequested ? (offline() ? cancellationCopy.offline : cancelling ? cancellationCopy.stopping : cancellationCopy.failed) : offline() ? recoveryCopy.offline : recoveryCopy.waiting;
    updateSelection();
  }
  function resumePending(manual = false) {
    if (!pending || busy || cancelling || adding || resetting) return;
    if (!manual && (pending.paused || Date.now() < recoveryRetryAfter || document.visibilityState === 'hidden')) return;
    if (offline()) { setBusy(false); return; }
    if (pending.cancelRequested) return cancelTurn(pending);
    pending.paused = false; pending.recover = true;
    return runTurn(pending);
  }
  function appendMessage(item) {
    const follow = el('history').scrollHeight - el('history').scrollTop - el('history').clientHeight < 90;
    const node = document.createElement('div'); node.className = `fp-assistant__message fp-assistant__message--${item.role}`;
    if (item.turnId) node.dataset.assistantTurn = item.turnId;
    if (item.stopped) { const note = document.createElement('small'); note.className = 'fp-assistant__upload-note'; note.dataset.assistantStopped = ''; note.textContent = cancellationCopy.stopped; node.append(note); }
    if (item.text) { const bubble = document.createElement('p'); bubble.className = 'fp-assistant__bubble'; bubble.textContent = item.role === 'assistant' ? window.FivePointI18n.assistantText(item.text) : item.text; node.append(bubble); }
    if (item.photo) {
      if (safePhotoPreview(item.photo)) { const image = document.createElement('img'); image.src = item.photo; image.alt = window.FivePointI18n.t("Your fabric reference"); image.className = 'fp-assistant__reference'; node.classList.add('has-photo'); node.append(image); }
      else { const caption = document.createElement('p'); caption.textContent = window.FivePointI18n.t("Photo reference"); node.append(caption); }
    }
    el('welcome').hidden = true;
    el('messages').append(node);
    if (follow || item.role === 'user') el('history').scrollTop = el('history').scrollHeight;
    else el('latest').hidden = false;
    return node;
  }
  async function json(url, options = {}) {
    const response = await fetch(url, { mode: 'cors', credentials: 'omit', ...options });
    let data;
    try { data = await response.json(); }
    catch { throw new Error(window.FivePointI18n.t("The assistant returned an unreadable reply. Please check the reply before sending again.")); }
    if (!response.ok) { const e = new Error(typeof data.message === 'string' ? window.FivePointI18n.assistantText(data.message, 'error') : window.FivePointI18n.t("The assistant could not complete this request. Please try again.")); e.status = response.status; throw e; }
    return data;
  }
  async function getCards(handles, signal, isCurrent = () => true) {
    if (!handles.length) return [];
    if (handles.length > 20 || !handles.every(handleOK)) throw new Error(window.FivePointI18n.t("The product response was invalid."));
    const url = new URL(root.dataset.searchUrl, location.origin);
    url.searchParams.set('q', handles.join('|')); url.searchParams.set('section_id', 'fivepoint-finder-products');
    const timeout = AbortSignal.timeout(15000);
    const boundedSignal = signal ? AbortSignal.any([signal, timeout]) : timeout;
    let response, html;
    try {
      response = await fetch(url, { credentials: 'same-origin', signal: boundedSignal });
      if (response.ok) html = await response.text();
    } catch (e) {
      if (timeout.aborted && !signal?.aborted) throw new Error(window.FivePointI18n.t("Product details could not be loaded. Please retry."));
      throw e;
    }
    if (!response.ok) throw new Error(window.FivePointI18n.t("Product details could not be loaded. Please retry."));
    const doc = new DOMParser().parseFromString(html, 'text/html');
    if (!doc.querySelector('[data-finder-product-batch]')) throw new Error(window.FivePointI18n.t("Product details are temporarily unavailable."));
    const found = new Map([...doc.querySelectorAll('[data-finder-product]')].map(n => [n.dataset.handle, n]));
    if (!isCurrent()) return [];
    return handles.flatMap(handle => {
      cards.delete(handle);
      const n = found.get(handle); if (!n) return []; // A product removed since recommendation is legitimately unavailable.
      const node = document.importNode(n, true);
      node.querySelectorAll('script').forEach(script => script.remove());
      node.querySelectorAll('.fp-card__ph img').forEach(img => { img.sizes = '(max-width:350px) 85vw, 200px'; });
      cards.set(handle, { node, handle, variant: node.dataset.variant, quickship: node.dataset.quickship === 'true', price: node.dataset.price === undefined ? null : Number(node.dataset.price), name: node.querySelector('.fp-card__name')?.textContent.trim() || handle });
      return [node];
    });
  }
  async function renderRecommendations(item, container, cached = false, isCurrent = () => true) {
    if (!item.handles?.length) return;
    const grid = document.createElement('div'); grid.className = 'fp-assistant__cards';
    const productNodes = cached ? item.handles.flatMap(h => cards.has(h) ? [cards.get(h).node.cloneNode(true)] : []) : await getCards(item.handles, undefined, isCurrent);
    if (!isCurrent()) return;
    productNodes.forEach(node => {
      node.querySelectorAll('.fp-assistant__reason').forEach(reason => reason.remove());
      const reason = item.reasons?.find(r => r.handle === node.dataset.handle)?.text;
      if (reason) { const p = document.createElement('p'); p.className = 'fp-assistant__reason'; p.textContent = window.FivePointI18n.assistantText(reason, 'reason'); node.append(p); }
      grid.append(node);
    });
    container.querySelector('.fp-assistant__cards')?.remove(); container.append(grid);
    if (!productNodes.length) { const p = document.createElement('p'); p.textContent = window.FivePointI18n.t("These products are no longer available. Please ask for another selection."); container.append(p); }
    updateSelection();
  }
  function updateSelection() {
    const history = el('history');
    const wasAtEnd = history.scrollHeight - history.scrollTop - history.clientHeight < 90;
    const selected = conversation.selected.map(h => cards.get(h)).filter(Boolean);
    root.querySelectorAll('[data-finder-product]').forEach(n => {
      const input = n.querySelector('[data-finder-pick]');
      const p = cards.get(n.dataset.handle);
      const valid = /^\d+$/.test(p?.variant || '') && Number.isSafeInteger(p?.price);
      input.checked = conversation.selected.includes(n.dataset.handle);
      input.disabled = !valid || busy || adding || uncertainCart;
      n.classList.toggle('is-selected', input.checked);
    });
    el('selection').hidden = !conversation.selected.length;
    el('count').textContent = window.FivePointI18n.t("{{p0}} colour card{{p1}}", [conversation.selected.length, conversation.selected.length === 1 ? '' : 's']);
    const total = selected.reduce((sum, p) => sum + (Number.isSafeInteger(p.price) ? p.price : 0), 0);
    el('total').textContent = money.format(total / 100);
    const absent = conversation.selected.filter(h => !latestHandles.has(h)).length;
    el('outside').hidden = !absent; el('outside').textContent = window.FivePointI18n.t("{{p0}} outside the latest results. Review selection.", [absent]);
    const unavailable = selected.length !== conversation.selected.length || selected.some(p => !/^\d+$/.test(p.variant || '') || !Number.isSafeInteger(p.price));
    const overBudget = Number.isSafeInteger(conversation.cardBudget) && total > conversation.cardBudget;
    el('add').disabled = unavailable || overBudget || busy || adding || uncertainCart;
    el('add').textContent = adding ? window.FivePointI18n.t("Adding…") : unavailable ? window.FivePointI18n.t("Review unavailable cards") : overBudget ? window.FivePointI18n.t("Over colour-card budget") : window.FivePointI18n.t("Add selected to cart");
    el('selected').replaceChildren(...conversation.selected.map(handle => {
      const row = document.createElement('div'), label = document.createElement('span');
      const product = cards.get(handle); label.textContent = product && Number.isSafeInteger(product.price) ? `${product.name} · ${money.format(product.price / 100)}` : window.FivePointI18n.t("{{p0}} · unavailable", [product?.name || handle]);
      const remove = document.createElement('button'); remove.type = 'button'; remove.textContent = window.FivePointI18n.t("Remove"); remove.dataset.assistantRemove = handle; remove.disabled = adding;
      row.append(label, remove); return row;
    }));
    if (wasAtEnd) history.scrollTop = history.scrollHeight;
  }
  async function ensureSession() {
    if (conversation.token) return;
    if (!sessionCreation) {
      const owner = conversation;
      sessionCreation = (async () => {
        const result = await json(`${endpoint}/sessions`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ country: root.dataset.country, currency: root.dataset.currency, language: root.dataset.locale }), signal: AbortSignal.timeout(15000) });
        if (typeof result.session_token !== 'string' || !Number.isInteger(result.revision)) throw new Error(window.FivePointI18n.t("The assistant session could not be created."));
        if (conversation !== owner) return;
        owner.token = result.session_token; owner.revision = result.revision; owner.expires = result.expires_at * 1000; persist();
      })().finally(() => { sessionCreation = null; });
    }
    return sessionCreation;
  }
  function stoppedTurn(turn) {
    const item = conversation.messages.find(m => m.role === 'user' && m.turnId === turn.id) || conversation.messages.at(-1);
    if (!item || item.role !== 'user') return;
    item.stopped = true;
    const node = el('messages').querySelector(`[data-assistant-turn="${turn.id}"]`) || [...el('messages').querySelectorAll('.fp-assistant__message--user')].at(-1);
    if (node && !node.querySelector('[data-assistant-stopped]')) {
      const note = document.createElement('small'); note.className = 'fp-assistant__upload-note'; note.dataset.assistantStopped = ''; note.textContent = cancellationCopy.stopped; node.append(note);
    }
  }
  async function cancelTurn(turn = pending) {
    if (!turn || pending !== turn || cancelling || adding || resetting) return;
    const owner = conversation;
    turn.cancelRequested = true; turn.paused = false; cancelling = true;
    previewEpoch++; request?.abort(); request = null;
    turn.cleanup?.(); setBusy(false); error(''); persist();
    try {
      // A cancelled first message may still be creating its session. Share that
      // creation, then fence the unsent run before it can POST a stale turn.
      if (sessionCreation) { try { await sessionCreation; } catch { /* No turn could be sent. */ } }
      if (conversation !== owner || pending !== turn) return;
      let result = { status: 'cancelled', revision: owner.revision, expires_at: owner.expires / 1000 };
      if (owner.token && (turn.submitted || turn.recover)) {
        if (offline()) throw new TypeError('offline cancellation');
        result = await json(`${endpoint}/turns/${turn.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${owner.token}` }, signal: AbortSignal.timeout(15000) });
      }
      if (conversation !== owner || pending !== turn) return;
      if (!['cancelled', 'completed'].includes(result.status) || !Number.isInteger(result.revision) || result.revision < owner.revision || !Number.isFinite(result.expires_at)) throw new Error('invalid cancellation result');
      // Completion may win the server lock just before cancellation. Adopt its
      // revision without rendering a late reply over the shopper's correction.
      owner.revision = result.revision; owner.expires = result.expires_at * 1000;
      pending = null; stoppedTurn(turn);
      if (!el('input').value && !turn.action) { el('input').value = turn.text || ''; saveDraft(); }
      if (turn.file && !file) {
        file = turn.file; photoPreview = turn.photoPreview || null;
        previewURL = URL.createObjectURL(file); photoURLs.add(previewURL);
        el('preview').src = previewURL; el('attachment').hidden = false;
      } else if (turn.hasPhoto && !turn.file) photoNotice(cancellationCopy.photo);
      persist(); announce(result.status === 'completed' ? '' : cancellationCopy.done); el('input').focus();
    } catch (e) {
      if (conversation !== owner || pending !== turn) return;
      recoveryRetryAfter = Date.now() + 5000;
      if (e.status === 401) { pending = null; el('reset').hidden = false; error(e.message); }
      else if (e.status === 409) {
        turn.cancelRequested = false; turn.cancelBlocked = true; turn.recover = true;
        error(e.message);
      } else error(offline() ? cancellationCopy.offline : cancellationCopy.failed);
      persist();
    } finally { cancelling = false; if (conversation === owner) setBusy(false); }
  }
  // Transient events are display-only; the durable complete event is the sole commit.
  function streamPreview(loading) {
    const wrapper = document.createElement('div'); wrapper.className = 'fp-assistant__message fp-assistant__message--assistant'; wrapper.hidden = true;
    const draft = document.createElement('p'); draft.className = 'fp-assistant__bubble';
    const grid = document.createElement('div'); grid.className = 'fp-assistant__cards';
    wrapper.append(draft, grid); el('messages').append(wrapper);
    const turnEpoch = previewEpoch;
    let candidates = [], chosen = null, generation = 0, controller = null, inFlight = null, committed = false, retry = null, owner = null;
    const active = () => turnEpoch === previewEpoch && (!owner || (conversation === owner.conversation && conversation.token === owner.token && conversation.revision === owner.revision));
    const update = fn => {
      const history = el('history'); const follow = history.scrollHeight - history.scrollTop - history.clientHeight < 90;
      fn(); if (follow) history.scrollTop = history.scrollHeight; else el('latest').hidden = false;
    };
    const same = (a, b) => a && b && a.length === b.length && a.every((x, i) => x === b[i]);
    const invalidate = () => { generation++; controller?.abort(); controller = null; inFlight = null; chosen = null; retry?.remove(); retry = null; };
    const clear = () => { invalidate(); candidates = []; update(() => { wrapper.hidden = true; draft.textContent = ''; grid.replaceChildren(); }); };
    const start = handles => {
      if (same(chosen, handles)) return inFlight;
      invalidate(); chosen = handles.slice(); const epoch = generation;
      update(() => grid.replaceChildren());
      if (!handles.length) { inFlight = Promise.resolve({ nodes: [] }); return inFlight; }
      controller = new AbortController();
      inFlight = getCards(handles, controller.signal, () => epoch === generation && active()).then(nodes => {
        if (epoch === generation && active()) { update(() => grid.replaceChildren(...nodes)); updateSelection(); }
        return { nodes };
      }, error => ({ error }));
      return inFlight;
    };
    const decorate = (item, nodes) => {
      nodes.forEach(node => {
        node.querySelectorAll('.fp-assistant__reason').forEach(reason => reason.remove());
        const reason = item.reasons?.find(r => r.handle === node.dataset.handle)?.text;
        if (reason) { const p = document.createElement('p'); p.className = 'fp-assistant__reason'; p.textContent = window.FivePointI18n.assistantText(reason, 'reason'); node.append(p); }
      });
      if (item.handles.length && !nodes.length) { const p = document.createElement('p'); p.textContent = window.FivePointI18n.t("These products are no longer available. Please ask for another selection."); wrapper.append(p); }
    };
    const finishCards = async item => {
      const wait = start(item.handles), epoch = generation; const outcome = await wait;
      if (epoch !== generation || !active()) return;
      if (outcome.error) {
        retry = document.createElement('button'); retry.type = 'button'; retry.className = 'fp-assistant__recovery'; retry.textContent = window.FivePointI18n.t("Reload product details");
        retry.addEventListener('click', async () => { if (!active()) return; retry?.remove(); retry = null; chosen = null; error(''); await finishCards(item); });
        wrapper.append(retry); error(outcome.error.message); return;
      }
      update(() => decorate(item, outcome.nodes));
    };
    return {
      clear,
      remove() { if (!committed) { invalidate(); wrapper.remove(); } },
      async finish(item) {
        owner = { conversation, token: conversation.token, revision: conversation.revision };
        committed = true;
        update(() => { wrapper.hidden = false; draft.textContent = window.FivePointI18n.assistantText(item.text); });
        await finishCards(item); if (active()) renderActions(item, wrapper);
      },
      event(type, data) {
        if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('Invalid stream event');
        if (type === 'reset') { clear(); return; }
        if (['accepted', 'phase'].includes(type) && Number.isSafeInteger(data.expires_at) && data.expires_at * 1000 > Date.now() && data.expires_at * 1000 <= Date.now() + 86410000) {
          conversation.expires = data.expires_at * 1000; persist();
        }
        if (type === 'accepted') return;
        if (type === 'phase') {
          if (typeof data.stage !== 'string' || data.stage.length > 80) throw new Error('Invalid stream phase');
          if (pending) pending.queueStage = ['queued', 'paused', 'running'].includes(data.stage) ? data.stage : 'running';
          loading.textContent = data.stage === 'queued' ? ux.queued
            : data.stage === 'paused' ? ux.paused
            : data.stage === 'create_support_ticket' ? ux.ticket
            : ['search_products', 'search_by_image', 'get_product_details'].includes(data.stage) ? window.FivePointI18n.t("Finding fabrics for your project…") : ux.processing;
          return;
        }
        if (type === 'products') {
          if (!Array.isArray(data.products) || data.products.length > 4 || !data.products.every(p => p && typeof p === 'object' && handleOK(p.handle))) throw new Error('Invalid stream products');
          candidates = data.products.map(p => p.handle);
          if (data.stage === 'selected') {
            start(candidates);
            if (candidates.length) update(() => { wrapper.hidden = false; draft.textContent = ux.processing; });
          }
          return;
        }
        if (type === 'draft') {
          if (typeof data.text !== 'string' || data.text.length > 65536) throw new Error('Invalid stream draft');
          update(() => { wrapper.hidden = false; draft.textContent = data.text; });
          if (chosen === null) start(candidates); return;
        }
      }
    };
  }
  async function postTurn(data, signal, preview) {
    const response = await fetch(`${endpoint}/turns`, { mode: 'cors', credentials: 'omit', method: 'POST', headers: { Authorization: `Bearer ${conversation.token}`, Accept: 'text/event-stream' }, body: data, signal });
    if (!response.ok || !/^text\/event-stream(?:\s*;|\s*$)/i.test(response.headers.get('content-type') || '')) {
      let body; try { body = await response.json(); } catch { const e = new Error(window.FivePointI18n.t("The assistant returned an unreadable reply. Please check the reply before sending again.")); if (!response.ok) e.status = response.status; throw e; }
      if (!response.ok) { const e = new Error(typeof body.message === 'string' ? window.FivePointI18n.assistantText(body.message, 'error') : window.FivePointI18n.t("The assistant could not complete this request. Please try again.")); e.status = response.status; throw e; }
      return body;
    }
    if (!response.body?.getReader) throw new Error('Stream unavailable');
    const reader = response.body.getReader(), decoder = new TextDecoder('utf-8', { fatal: true });
    let buffer = '', event = '', lines = [], eventSize = 0, total = 0, result;
    const dispatch = () => {
      if (!lines.length) { event = ''; return; }
      let payload; try { payload = JSON.parse(lines.join('\n')); } catch { throw new Error('Invalid stream event'); }
      const type = event || 'message'; event = ''; lines = []; eventSize = 0;
      if (type === 'complete') { if (!payload || typeof payload !== 'object' || Array.isArray(payload)) throw new Error('Invalid stream completion'); result = payload; return; }
      if (type === 'error') { const e = new Error(window.FivePointI18n.t("The assistant could not complete this request. Please try again.")); e.status = [502, 503].includes(payload?.status) ? payload.status : 502; throw e; }
      if (['accepted', 'phase', 'products', 'draft', 'reset'].includes(type)) preview.event(type, payload);
    };
    try {
      while (!result) {
        const chunk = await reader.read();
        if (chunk.done) { buffer += decoder.decode(); if (buffer.endsWith('\r')) buffer += '\n'; }
        else {
          total += chunk.value.byteLength; if (total > 8388608) throw new Error('Stream too large');
          buffer += decoder.decode(chunk.value, { stream: true });
        }
        if (buffer.length > 262144) throw new Error('Stream line too large');
        let newline;
        while ((newline = buffer.search(/[\r\n]/)) !== -1 && !result) {
          if (buffer[newline] === '\r' && newline === buffer.length - 1) break; // CRLF may straddle chunks.
          const width = buffer[newline] === '\r' && buffer[newline + 1] === '\n' ? 2 : 1;
          const line = buffer.slice(0, newline); buffer = buffer.slice(newline + width);
          if (!line) { dispatch(); continue; }
          if (line.startsWith(':')) continue;
          const colon = line.indexOf(':'), field = colon < 0 ? line : line.slice(0, colon);
          let value = colon < 0 ? '' : line.slice(colon + 1); if (value.startsWith(' ')) value = value.slice(1);
          eventSize += line.length; if (eventSize > 262144) throw new Error('Stream event too large');
          if (field === 'event') event = value; else if (field === 'data') lines.push(value);
        }
        if (chunk.done && !result) throw new Error('Interrupted stream');
      }
      return result;
    } finally { try { await reader.cancel(); } catch { /* A disconnected stream is already closed. */ } }
  }
  function waitForReplyPoll(signal) {
    return new Promise((resolve, reject) => {
      const aborted = () => { clearTimeout(timer); reject(new DOMException('Stopped waiting', 'AbortError')); };
      const timer = setTimeout(() => { signal.removeEventListener('abort', aborted); resolve(); }, 3000);
      if (signal.aborted) aborted(); else signal.addEventListener('abort', aborted, { once: true });
    });
  }
  async function recoverTurn(turn, signal, preview) {
    // Recover only this accepted turn. Never repeat a model request or a cart
    // mutation when a proxy/browser loses the original response.
    for (;;) {
      let status;
      try {
        status = await json(`${endpoint}/turns/${turn.id}`, { headers: { Authorization: `Bearer ${conversation.token}` }, signal: AbortSignal.any([signal, AbortSignal.timeout(15000)]) });
      } catch (e) {
        if (signal.aborted) throw new DOMException('Stopped waiting', 'AbortError');
        if (e.status && e.status < 500 && e.status !== 429) throw e;
        await waitForReplyPoll(signal); continue;
      }
      if (status.status === 'completed') return status;
      if (status.status === 'pending') {
        if (['queued', 'paused', 'running'].includes(status.queue_stage)) preview?.event('phase', { stage: status.queue_stage, expires_at: status.expires_at });
        await waitForReplyPoll(signal); continue;
      }
      if (status.status === 'cancelled') { turn.cancelRequested = true; throw Object.assign(new Error(cancellationCopy.done), { cancelled: true }); }
      if (status.status === 'failed') { pending = null; persist(); throw new Error(window.FivePointI18n.t("{{p0}} You can send a new message.", [window.FivePointI18n.assistantText(status.message, 'error')])); }
      if (status.status === 'missing') return null;
      throw new Error(window.FivePointI18n.t("The previous reply could not be checked."));
    }
  }
  async function runTurn(turn) {
    if (!turn || busy || cancelling || resetting) return;
    previewEpoch++;
    const owner = conversation, epoch = previewEpoch;
    const isCurrent = () => conversation === owner && previewEpoch === epoch;
    let loading = null, preview = null, notReceived = false;
    try {
      setBusy(true); error('');
      loading = document.createElement('p'); loading.className = 'fp-assistant__loading'; loading.textContent = ux.processing; el('messages').append(loading);
      preview = streamPreview(loading);
      turn.cleanup = () => { preview?.remove(); loading?.remove(); };
      if (el('history').scrollHeight - el('history').scrollTop - el('history').clientHeight < 150) el('history').scrollTop = el('history').scrollHeight;
      if (turn.recover) {
        if (!conversation.token) throw new Error(window.FivePointI18n.t("The previous reply could not be checked."));
        loading.textContent = recoveryCopy.checking;
      } else await ensureSession();
      if (!isCurrent()) return;
      if (turn.paused) throw new DOMException('Stopped waiting', 'AbortError');
      let result;
      const data = new FormData(); data.set('text', turn.text); data.set('action', turn.action); data.set('revision', String(conversation.revision)); data.set('turn_id', turn.id);
      if (turn.page?.handle) data.set('page_handle', turn.page.handle);
      if (turn.page?.variant) data.set('page_variant', turn.page.variant);
      if (turn.file) data.set('file', turn.file, 'fabric-reference.jpg');
      const controller = new AbortController(); request = controller;
      const signal = controller.signal;
      const timer = setTimeout(() => controller.abort(), 265000);
      try {
        if (turn.recover) {
          result = await recoverTurn(turn, signal, preview);
          if (!result) { notReceived = true; pending = null; persist(); throw new Error(turn.hasPhoto && !turn.file ? window.FivePointI18n.t("Your photo was not received. Please attach it again and send a new message.") : window.FivePointI18n.t("The previous reply could not be checked.")); }
        }
        if (!result) {
          try {
            turn.submitted = true; persist();
            result = await postTurn(data, signal, preview);
            if (typeof result.reply !== 'string' || !Array.isArray(result.handles) || result.handles.length > 4 || !result.handles.every(handleOK) || result.revision !== conversation.revision + 1) throw new Error(window.FivePointI18n.t("The assistant returned an incomplete reply."));
          }
          catch (e) {
            if (signal.aborted || (e.status && e.status < 500 && e.status !== 409)) throw e;
            preview.clear();
            result = await recoverTurn(turn, signal, preview);
            if (!result) throw e;
          }
        }
      }
      finally { clearTimeout(timer); }
      if (!isCurrent()) return;
      if (typeof result.reply !== 'string' || !Array.isArray(result.handles) || result.handles.length > 4 || !result.handles.every(handleOK) || result.revision !== conversation.revision + 1) throw new Error(window.FivePointI18n.t("The assistant returned an incomplete reply."));
      conversation.revision = result.revision; conversation.expires = result.expires_at * 1000;
      conversation.cardBudget = Number.isSafeInteger(result.card_budget_cents) ? result.card_budget_cents : null;
      conversation.dispatchOnly = result.dispatch_only === true;
      pending = null; el('retry').hidden = true;
      const item = { role: 'assistant', text: result.reply, handles: result.handles, reasons: result.reasons || [], actions: result.actions || [] };
      conversation.messages.push(item); latestHandles = new Set(result.handles); persist();
      // Completion is observed once here, never when restoring messages/cards.
      // No conversation text, session token, photo or mutable elapsed time is sent.
      window.FivePointEvents?.emit('fp_agent_turn_complete',{turn_id:turn.id,result:result.handles.length?'success':'empty',result_count:result.handles.length},{event_id:'agent-turn:'+turn.id,occurred_at:new Date().toISOString(),source_surface:'agent',component_id:'agent-turn'});
      await preview.finish(item); announce(window.FivePointI18n.assistantText(result.reply));
      el('input').placeholder = window.FivePointI18n.t("Tell me what to change…");
    } catch (e) {
      if (!isCurrent()) return;
      if (e.cancelled) { setBusy(false); await cancelTurn(turn); return; }
      if (pending) pending.recover = true;
      recoveryRetryAfter = Date.now() + 5000;
      // No session means /turns could not have been sent. Do not trap this
      // unsent message behind recovery, which requires an existing session.
      if (!conversation.token) pending = null;
      if ((!conversation.token || notReceived) && turn.file && !file) {
        file = turn.file; photoPreview = turn.photoPreview || null;
        previewURL = URL.createObjectURL(file); photoURLs.add(previewURL);
        el('preview').src = previewURL; el('attachment').hidden = false;
      }
      window.FivePointEvents?.emit('fp_interaction_error',{operation:'agent',error_code:e.name==='AbortError'?'timeout':e.status===401?'unauthorized':e instanceof TypeError?'network':'upstream'},{source_surface:'agent',component_id:'agent-turn'});
      if (e.status === 401) { pending = null; el('reset').hidden = false; }
      if (!pending && !el('input').value && !turn.action) { el('input').value = turn.text || ''; saveDraft(); }
      persist();
      error(e.name === 'AbortError' && pending?.paused ? '' : e.name === 'AbortError' ? (pending?.queueStage === 'queued' || pending?.queueStage === 'paused' ? ux.checkQueued : window.FivePointI18n.t("Stopped waiting. Check the reply before sending another message.")) : e.message);
      el('retry').hidden = !pending;
    } finally {
      if (isCurrent()) request = null;
      try { preview?.remove(); } finally {
        try { loading?.remove(); } finally { if (isCurrent()) setBusy(false); }
      }
    }
  }
  function renderActions(item, message) {
    const actions = document.createElement('div'); actions.className = 'fp-assistant__actions';
    const labels = { more: window.FivePointI18n.t("Show more options"), cheaper: window.FivePointI18n.t("Lower fabric price"), relax: window.FivePointI18n.t("Adjust requirements"), metre: window.FivePointI18n.t("Per metre"), cards: window.FivePointI18n.t("Colour cards, in total") };
    // The owner paused the dispatch promotion; omit it from fresh and restored actions.
    for (const action of (item.actions || []).filter(action => labels[action]).slice(0,2)) {
      const node = button(labels[action], action); node.disabled = busy || Boolean(pending) || adding || resetting; actions.append(node);
    }
    message.append(actions);
  }
  async function showProducts(item, message, cached = false, isCurrent = () => true) {
    try { await renderRecommendations(item, message, cached, isCurrent); }
    catch (e) {
      if (!isCurrent()) return;
      const retryCards = document.createElement('button'); retryCards.type = 'button'; retryCards.className = 'fp-assistant__recovery'; retryCards.textContent = window.FivePointI18n.t("Reload product details");
      retryCards.addEventListener('click', async () => { retryCards.remove(); error(''); await showProducts(item, message, false, isCurrent); });
      message.append(retryCards); error(e.message);
    }
  }
  function currentFabricPage() {
    const handle = location.pathname.match(/\/products\/([a-z0-9][a-z0-9-]{0,127})\/?$/)?.[1];
    if (!handle) return null;
    const selected = document.querySelector('main [data-fp-variant-id]')?.value || '';
    return { handle, variant: /^[1-9][0-9]{0,19}$/.test(selected) ? selected : '' };
  }
  async function submit(action = '') {
    if (busy || cancelling || adding || resetting) return;
    if (preparingPhoto) { photoNotice(ux.preparing); return; }
    if (pending) { await resumePending(true); return; }
    const text = action ? '' : el('input').value.trim();
    if (!action && !text && !file) return;
    pending = { id: crypto.randomUUID(), text, action, page: currentFabricPage(), file: action ? null : file, photoPreview: action ? null : photoPreview };
    const labels = { more: window.FivePointI18n.t("Show me more options."), cheaper: window.FivePointI18n.t("Show lower-priced fabric options."), relax: window.FivePointI18n.t("Which requirement could I change?"), metre: window.FivePointI18n.t("My budget is per metre of fabric."), cards: window.FivePointI18n.t("My budget is for all selected colour cards.") };
    const item = { role: 'user', turnId: pending.id, text: labels[action] || text, photo: pending.file ? (photoPreview || 'attached') : null };
    conversation.messages.push(item); persist(); appendMessage(item);
    // Consume only the submitted composer. Completion must never erase a newer draft.
    if (!action) {
      el('input').value = ''; saveDraft(); clearAttachment();
      el('input').style.height = ''; el('input').style.overflowY = 'hidden';
    }
    await runTurn(pending);
  }
  function clearAttachment() {
    attachmentEpoch++; preparingPhoto = false; file = null; photoPreview = null;
    if (previewURL) { URL.revokeObjectURL(previewURL); photoURLs.delete(previewURL); previewURL = null; }
    el('preview').removeAttribute('src'); el('attachment').hidden = true; el('file').value = ''; el('upload-note').hidden = true;
    el('send').disabled = busy || Boolean(pending) || adding || resetting;
  }
  function makePhotoPreview(img) {
    const canvas = document.createElement('canvas');
    for (const side of [384, 256, 160]) {
      const scale = Math.min(1, side / Math.max(img.naturalWidth, img.naturalHeight));
      canvas.width = Math.max(1, Math.round(img.naturalWidth * scale)); canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
      const ctx = canvas.getContext('2d'); ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height); ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      for (const quality of [.72, .5, .3]) {
        const value = canvas.toDataURL('image/jpeg', quality);
        if (safePhotoPreview(value)) return value;
      }
    }
    return null;
  }
  async function preparePhoto(candidate) {
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(candidate.type) || candidate.size > 8 * 1024 * 1024 || !candidate.size) throw new Error(window.FivePointI18n.t("Choose a JPG, PNG or WebP smaller than 8 MB."));
    const url = URL.createObjectURL(candidate);
    try {
      const img = new Image(); img.src = url; await img.decode();
      if (img.naturalWidth * img.naturalHeight > 40000000) throw new Error(window.FivePointI18n.t("Choose a photo under 40 megapixels."));
      const scale = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight));
      const canvas = document.createElement('canvas'); canvas.width = Math.round(img.naturalWidth * scale); canvas.height = Math.round(img.naturalHeight * scale);
      const ctx = canvas.getContext('2d'); ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height); ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', .9));
      if (!blob) throw new Error(window.FivePointI18n.t("The photo could not be opened."));
      let preview = null; try { preview = makePhotoPreview(img); } catch { /* Preview failure must not change the upload. */ }
      return { blob, preview };
    } finally { URL.revokeObjectURL(url); }
  }
  async function addSelected() {
    if (busy || adding || uncertainCart || el('add').disabled) return;
    const bag = document.getElementById('cart-drawer');
    if (typeof bag?.addItems !== 'function') { error(window.FivePointI18n.t("The shopping bag is unavailable. Please reload before adding.")); return; }
    adding = true; updateSelection(); error('');
    let mutationStarted = false;
    try {
      // Refresh the selected variants and prices immediately before the actual mutation.
      const selected = [...conversation.selected], before = selected.map(h => ({ handle: h, price: cards.get(h)?.price, variant: cards.get(h)?.variant }));
      await getCards(selected);
      if (selected.some(h => !/^\d+$/.test(cards.get(h)?.variant || ''))) throw new Error(window.FivePointI18n.t("A selected colour card is no longer available. Review your selection."));
      if (conversation.dispatchOnly && selected.some(h => !cards.get(h).quickship)) throw new Error('A selected product does not meet your requested dispatch timing. Remove it or change your requirement.');
      if (before.some(p => p.price !== cards.get(p.handle).price || p.variant !== cards.get(p.handle).variant)) {
        el('selected').hidden = false; throw new Error(window.FivePointI18n.t("A selected price or option changed. Review the updated selection before adding."));
      }
      mutationStarted = true;
      await bag.addItems(selected.map(h => ({ id: cards.get(h).variant, quantity: 1 })),{source_surface:'agent',component_id:'agent-batch-add'});
      conversation.selected = []; persist(); close(); announce(window.FivePointI18n.t("Colour cards added to your shopping bag."));
    } catch (e) {
      uncertainCart = mutationStarted; error(mutationStarted ? window.FivePointI18n.t("{{p0}} Review your shopping bag before trying again.", [e.message]) : e.message); el('cart-review').hidden = !uncertainCart;
    } finally { adding = false; persist(); updateSelection(); }
  }
  async function reset() {
    if (busy || cancelling || adding || resetting) return;
    resetting = true; setBusy(false);
    previewEpoch++;
    try {
    if (conversation.token) {
      try { await json(`${endpoint}/sessions/current`, { method: 'DELETE', headers: { Authorization: `Bearer ${conversation.token}` }, signal: AbortSignal.timeout(10000) }); }
      catch (e) { if (e.status !== 401) { error(e.message); return; } }
    }
    conversation = { token: null, revision: 0, messages: [], selected: [], expires: 0 }; cards.clear(); latestHandles.clear(); pending = null;
    uncertainCart = false; el('cart-review').hidden = true; el('retry').hidden = true; el('reset').hidden = true; el('messages').replaceChildren(); el('welcome').hidden = false;
    photoURLs.forEach(url => URL.revokeObjectURL(url)); photoURLs.clear();
    clearAttachment(); photoNotice(''); el('input').value = ''; saveDraft(); error(''); persist(); updateSelection();
    el('input').style.height = ''; el('input').placeholder = emptyPlaceholder;
    root.dispatchEvent(new Event('fivepoint:assistant-reset'));
    } finally { resetting = false; setBusy(false); }
  }
  const retry = button(window.FivePointI18n.t("Check reply"), ''); retry.removeAttribute('data-assistant-action'); retry.dataset.assistantRetry = ''; retry.className = 'fp-assistant__recovery'; retry.hidden = true; el('error').after(retry);
  root.addEventListener('click', async event => {
    const target = event.target.closest('button'); if (!target) return;
    if (target.disabled) return;
    if (target.hasAttribute('data-assistant-open')) open(target);
    if (target.hasAttribute('data-assistant-close')) close();
    if (target.hasAttribute('data-assistant-dismiss')) { el('hint').hidden = true; try { sessionStorage.setItem('fivepoint-assistant-hint', 'dismissed'); } catch { /* Optional presentation state; no business fact depends on storage. */ } }
    if (target.hasAttribute('data-assistant-photo')) { el('file').click(); el('upload-note').hidden = false; }
    if (target.hasAttribute('data-assistant-remove-photo')) clearAttachment();
    if (target.hasAttribute('data-assistant-starter')) {
      if (target.dataset.assistantStarter === 'photo') { el('file').click(); el('upload-note').hidden = false; }
      else { el('input').value = target.dataset.assistantStarter === 'project' ? window.FivePointI18n.t("I’m making ") : window.FivePointI18n.t("Help me choose a fabric for "); saveDraft(); el('input').focus(); }
    }
    if (target.hasAttribute('data-assistant-action')) await submit(target.dataset.assistantAction);
    if (target.hasAttribute('data-assistant-retry')) await resumePending(true);
    if (target.hasAttribute('data-assistant-cancel')) await cancelTurn();
    if (target.hasAttribute('data-assistant-add')) await addSelected();
    if (target.hasAttribute('data-assistant-review')) el('selected').hidden = !el('selected').hidden;
    if (target.hasAttribute('data-assistant-remove')) { conversation.selected = conversation.selected.filter(h => h !== target.dataset.assistantRemove); persist(); updateSelection(); }
    if (target.hasAttribute('data-assistant-new')) el('reset').hidden = false;
    if (target.hasAttribute('data-assistant-reset-no')) el('reset').hidden = true;
    if (target.hasAttribute('data-assistant-reset-yes')) await reset();
    if (target.hasAttribute('data-assistant-latest')) { el('history').scrollTop = el('history').scrollHeight; el('latest').hidden = true; }
    if (target.hasAttribute('data-assistant-cart-review')) {
      const bag = document.getElementById('cart-drawer');
      if (typeof bag?.refreshAndOpen === 'function') {
        close(); await bag.refreshAndOpen();
        if (bag.querySelector('dialog[open]')) {
          conversation.selected = []; uncertainCart = false; el('cart-review').hidden = true; persist(); updateSelection();
          error(window.FivePointI18n.t("Previous selections cleared. Check quantities in your shopping bag before selecting again."));
        }
      }
    }
  });
  root.addEventListener('change', event => {
    if (!event.target.matches('[data-finder-pick]')) return;
    const handle = event.target.closest('[data-finder-product]').dataset.handle;
    conversation.selected = event.target.checked ? [...new Set([...conversation.selected, handle])].slice(0,12) : conversation.selected.filter(h => h !== handle);
    updateSelection(); persist();
  });
  el('file').addEventListener('change', async () => {
    if (busy || pending || adding || resetting || !el('file').files[0]) return;
    const candidate = el('file').files[0], epoch = ++attachmentEpoch;
    preparingPhoto = true; el('send').disabled = true;
    try {
      const prepared = await preparePhoto(candidate);
      if (epoch !== attachmentEpoch) return;
      if (previewURL) { URL.revokeObjectURL(previewURL); photoURLs.delete(previewURL); }
      file = prepared.blob; photoPreview = prepared.preview; previewURL = URL.createObjectURL(file); photoURLs.add(previewURL);
      el('preview').src = previewURL; el('attachment').hidden = false;
      if (!photoPreview) photoNotice(ux.preview);
      else if (previewNotice.textContent === ux.preparing) photoNotice('');
      error(conversation.selected.length ? window.FivePointI18n.t("Your selected colour cards will stay selected. Review them against the new photo before adding.") : '');
    } catch (e) { if (epoch === attachmentEpoch) error(e.message); }
    finally { if (epoch === attachmentEpoch) { preparingPhoto = false; setBusy(busy); } }
  });
  el('form').addEventListener('submit', event => { event.preventDefault(); submit(); });
  el('input').addEventListener('keydown', event => { if (event.key === 'Enter' && !event.shiftKey && !event.isComposing && !smallScreen.matches) { event.preventDefault(); submit(); } });
  el('input').addEventListener('input', () => { saveDraft(); el('input').style.height = 'auto'; const height = el('input').scrollHeight; el('input').style.height = `${Math.min(height,106)}px`; el('input').style.overflowY = height > 106 ? 'auto' : 'hidden'; });
  restoreState();
  restoreDraft(); setBusy(false);
  el('input').placeholder = conversation.messages.length ? window.FivePointI18n.t("Tell me what to change…") : emptyPlaceholder;
  try { if (sessionStorage.getItem('fivepoint-assistant-hint') === 'dismissed') el('hint').hidden = true; } catch { /* The optional hint remains visible if browser storage is disabled. */ }
  if (conversation.messages.length) {
    restoring = true;
    // Restore messages now; product requests wait until the shopper opens the assistant.
    const owner = conversation, revision = conversation.revision;
    const messages = [...conversation.messages], nodes = messages.map(item => appendMessage(item));
    restoreProducts = async () => {
      if (!restoring) return;
      restoring = false;
      const isCurrent = () => conversation === owner && conversation.revision === revision;
      if (conversation !== owner) return;
      try {
        const views = new Map();
        const last = messages.findLastIndex(item => item.handles?.length);
        for (let i = 0; i < messages.length; i++) {
          const item = messages[i];
          if (item.handles?.length) {
            const view = document.createElement('button'); view.type = 'button'; view.className = 'fp-assistant__recovery'; view.textContent = window.FivePointI18n.t("View these fabrics");
            view.addEventListener('click', async () => { view.remove(); await showProducts(item,nodes[i],false,() => conversation === owner && nodes[i].isConnected); }); nodes[i].append(view); views.set(i,view);
          } else if (i === messages.length - 1 && isCurrent()) renderActions(item,nodes[i]);
        }
        if (!isCurrent()) return;
        latestHandles = new Set(last >= 0 ? messages[last].handles : []);
        await getCards([...new Set([...conversation.selected, ...latestHandles])], undefined, isCurrent);
        if (!isCurrent()) return;
        if (last >= 0) { views.get(last)?.remove(); await showProducts(messages[last],nodes[last],true,isCurrent); if (isCurrent()) renderActions(messages[last],nodes[last]); }
        updateSelection();
      } catch (e) { if (isCurrent()) error(e.message); }
    };
  }
  if (shouldResumeView() && !bagDialog?.open) open(el('open'));
  // Lifecycle recovery reads a saved turn or retries its explicit cancellation.
  // Only the shopper can POST newly composed text; wakeups never replay it.
  resumePending();
  window.addEventListener('online', () => resumePending());
  window.addEventListener('focus', () => resumePending());
  window.addEventListener('offline', () => setBusy(busy));
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') resumePending(); });
  // Back/forward cache can restore an older DOM snapshot after another page
  // changed the conversation or closed it. Rebuild from current tab storage.
  window.addEventListener('pageshow', event => { if (event.persisted) location.reload(); });
})();
